/**
 * @module AdapterList
 * @package @geenius/adapters/solidjs-css
 * @description Renders the searchable adapter grid for the SolidJS CSS
 * variant. The list consumes provider status from context and delegates
 * selection events to the hosting page or dashboard shell.
 */

import type { AdapterDomain, AdapterStatusType } from "@geenius/adapters";
import { ADAPTER_DOMAINS, DOMAIN_LABELS } from "@geenius/adapters";
import type { JSX } from "solid-js";
import { createMemo, createSignal, For, Show } from "solid-js";
import { cx } from "../lib/cx";
import { createAdapters } from "../primitives";
import { AdapterCard } from "./AdapterCard";

/**
 * Props for rendering the SolidJS CSS adapter list and optional status filter controls.
 *
 * @property onSelect Optional callback invoked when a domain card is selected.
 * @property filterStatus Optional status filter applied before rendering the grid.
 * @property class Optional class name merged into the outer wrapper.
 */
export interface AdapterListProps {
  onSelect?: (domain: AdapterDomain) => void;
  filterStatus?: AdapterStatusType;
  class?: string;
}

/**
 * Renders the searchable SolidJS CSS adapter list backed by shared provider state.
 *
 * @param props List props including selection callback, status filtering, and wrapper class names.
 * @returns The adapter grid or an empty state when no entries are available.
 */
export function AdapterList(props: AdapterListProps): JSX.Element {
  const ctx = createAdapters();
  const [search, setSearch] = createSignal("");

  const filtered = createMemo(() => {
    let domains = [...ADAPTER_DOMAINS];
    const q = search().toLowerCase();
    if (q) {
      domains = domains.filter((d) => {
        return (
          (DOMAIN_LABELS[d] ?? d).toLowerCase().includes(q) ||
          d.includes(q) ||
          (ctx.statuses[d]?.provider?.toLowerCase().includes(q) ?? false)
        );
      });
    }
    if (props.filterStatus) {
      domains = domains.filter(
        (d) => ctx.statuses[d]?.status === props.filterStatus,
      );
    }
    return domains;
  });

  return (
    <Show
      when={!ctx.isLoading}
      fallback={
        <div
          class={cx(props.class)}
          role="status"
          aria-label="Loading adapters"
        >
          <div class="gn-adapters-grid">
            <For each={[1, 2, 3, 4]}>
              {() => (
                <div class="gn-adapters-skeleton gn-adapters-skeleton--card" />
              )}
            </For>
          </div>
        </div>
      }
    >
      <div class={cx(props.class)}>
        <input
          type="text"
          value={search()}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder="Search adapters..."
          class="gn-adapters-search"
          aria-label="Search adapters"
        />

        <Show
          when={filtered().length > 0}
          fallback={
            <div class="gn-adapters-empty">
              <p class="gn-adapters-empty__text">
                {search()
                  ? "No adapters match your search"
                  : "No adapters configured"}
              </p>
              <Show when={search()}>
                <button
                  class="gn-adapters-btn gn-adapters-btn--primary"
                  onClick={() => setSearch("")}
                  type="button"
                >
                  Clear search
                </button>
              </Show>
            </div>
          }
        >
          <ul class="gn-adapters-grid" aria-label="Adapter list">
            <For each={filtered()}>
              {(domain) => (
                <li>
                  <AdapterCard
                    domain={domain}
                    status={ctx.statuses[domain]}
                    onClick={() => props.onSelect?.(domain)}
                  />
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </Show>
  );
}
